import React, { memo, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import Icon from './Icon';

const IconGradient = memo(
    ({
        color,
        size = 'medium',
        gradientStops = [
            { offset: '0%', color: '#FF3B30' }, // Red 500
            { offset: '100%', color: '#FF8C00' }, // Orange 500
        ],
        ...props
    }) => {
        const spanRef = useRef();
        const gradientId = uuidv4();

        useEffect(() => {
            if (!spanRef.current) return;

            let defs = spanRef.current.querySelector('svg defs');

            if (!defs) {
                defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
                spanRef.current.querySelector('svg').appendChild(defs);
            }

            const gradient = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'linearGradient',
            );
            gradient.setAttribute('id', gradientId);

            gradientStops.forEach((stop) => {
                const newStop = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
                newStop.setAttribute('offset', stop.offset);
                newStop.setAttribute('stop-color', stop.color);
                gradient.appendChild(newStop);
            });

            defs.appendChild(gradient);
        }, [gradientId, gradientStops]);

        const iconProps = useMemo(
            () => ({
                ref: spanRef,
                color: `url(#${gradientId})`,
                ...props,
            }),
            [gradientId, props],
        );

        return <Icon {...iconProps} />;
    },
);

IconGradient.propTypes = {
    /** Custom className for styling purposes */
    className: PropTypes.string,
    /** Object with custom style overrides for inner elements */
    overrides: PropTypes.object,
    /** Size variant of the icon */
    size: PropTypes.oneOf(['raw', 'small', 'smallMedium', 'medium', 'large', 'big', 'huge']),
    /** Name of the icon to display from the icon library */
    name: PropTypes.string,
    /** Function called when the icon is clicked */
    onClick: PropTypes.func,
    /**
     * Array of gradient color stops that define the gradient colors.
     * Each stop specifies a position and color in the gradient.
     */
    gradientStops: PropTypes.arrayOf(
        PropTypes.shape({
            /** Percentage position of the color stop (e.g., '0%', '50%', '100%') */
            offset: PropTypes.string,
            /** Color value at this stop (hex, RGB, or named color) */
            color: PropTypes.string,
        }),
    ),
};

export default IconGradient;
